import { H3, Stack, Tag, Tooltip } from "@introist/react-foundation/v2";
import { Avatar } from "../../../../components/molecules";
import { Employee } from "../../hooks/useEmployees";
import { useMemo, useState } from "react";
import { api } from "../../../../services/rpc/RpcProvider";
import { EmployeeSyncLogDrawer } from "../../../datasources/containers/EmployeeSyncLog";
import moment from "moment";

type EmployeeHeaderProps = {
  employee: Employee;
};

export const EmployeeHeader = ({ employee }: EmployeeHeaderProps) => {
  const [syncLogOpen, setSyncLogOpen] = useState(false);

  const name = useMemo(() => {
    return employee.name ?? `${employee.firstname ?? ""} ${employee.lastname ?? ""}`;
  }, [employee]);

  const { data: syncLogs } = api.datasources.employeeSyncLogs.useQuery(
    { employeeId: employee.id ?? "" },
    {
      enabled: Boolean(employee.id)
    }
  );

  const latestSync = useMemo(() => {
    if (!syncLogs || syncLogs.length === 0) return null;
    return syncLogs[0];
  }, [syncLogs]);

  const syncBadgeText = useMemo(() => {
    if (!latestSync) return null;
    const relativeTime = moment(latestSync.timestamp).fromNow();
    return `Synced ${relativeTime}`;
  }, [latestSync]);

  const { data: freeze } = api.employees.v4.automationFreeze.useQuery(
    { id: employee.id ?? "" },
    { enabled: Boolean(employee.id) }
  );

  const freezeTooltip = useMemo(() => {
    if (!freeze) return "";
    const frozenAt = `Frozen ${moment(freeze.frozenAt).fromNow()}`;
    return freeze.reason ? `${frozenAt}: ${freeze.reason}` : frozenAt;
  }, [freeze]);

  return (
    <>
      <Stack alignItems="center" gap="medium">
        <Avatar nameOrEmail={name} size="huge" />
        <Stack gap="small" alignItems="center" justifyContent="center">
          <H3 style={{ margin: 0 }}>{name}</H3>
          {syncBadgeText && (
            <Tag
              colorVariant={latestSync?.status === "success" ? "success" : "danger"}
              startIcon="sync"
              onClick={() => setSyncLogOpen(true)}
            >
              {syncBadgeText}
            </Tag>
          )}
          {freeze && (
            <Tooltip tooltip={freezeTooltip}>
              <Tag colorVariant="warning" startIcon="pause">
                Automations frozen
              </Tag>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {employee.id && (
        <EmployeeSyncLogDrawer
          employeeId={employee.id}
          open={syncLogOpen}
          onClose={() => setSyncLogOpen(false)}
        />
      )}
    </>
  );
};
